import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { preReleaseService, PreReleaseProject, ProjectInterest } from '../../services/preReleaseService';
import { PricingService } from '../../services/pricingService';
import { showToast } from '../../utils/toast';

interface InterestModalProps {
  project: PreReleaseProject;
  existingInterest?: ProjectInterest | null;
  onClose: () => void;
  onInterestSubmitted: (interest: ProjectInterest) => void;
}

export const InterestModal: React.FC<InterestModalProps> = ({
  project,
  existingInterest,
  onClose,
  onInterestSubmitted
}) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [interestLevel, setInterestLevel] = useState(existingInterest?.interest_level || 3);
  const [willingToPay, setWillingToPay] = useState(existingInterest?.willing_to_pay || false);
  const [committedToBuy, setCommittedToBuy] = useState(existingInterest?.committed_to_buy || false);
  const [earlyBirdCommitment, setEarlyBirdCommitment] = useState(existingInterest?.early_bird_commitment || false);
  const [maxPriceWilling, setMaxPriceWilling] = useState(existingInterest?.max_price_willing || project.proposed_price || 0);
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [platformNotifications, setPlatformNotifications] = useState(true);
  const [customEmail, setCustomEmail] = useState('');
  const [showCustomEmail, setShowCustomEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user signed in via LinkedIn SSO and might need custom email
    if (user?.email && emailNotifications) {
      setCustomEmail(user.email);
    }
  }, [user, emailNotifications]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const interest = await preReleaseService.expressInterest(project.id, {
        interest_level: interestLevel,
        willing_to_pay: willingToPay,
        committed_to_buy: committedToBuy,
        early_bird_commitment: earlyBirdCommitment,
        max_price_willing: willingToPay ? maxPriceWilling : undefined
      });

      // Set notification preferences
      try {
        await preReleaseService.setNotificationPreferences(
          project.id,
          emailNotifications,
          platformNotifications
        );
      } catch (notificationError) {
        console.warn('Failed to set notification preferences:', notificationError);
        // Don't block the main flow if notification preferences fail
      }

      onInterestSubmitted(interest);
    } catch (err: any) {
      console.error('Error submitting interest:', err);

      // Provide more specific error messages
      if (err.message?.includes('duplicate key') || err.code === '23505') {
        setError('Your interest has been updated successfully!');
        // Still call the success callback since the interest was technically processed
        setTimeout(() => {
          onInterestSubmitted({
            id: '', // This will be updated by the parent component
            project_id: project.id,
            user_id: '', // This will be filled by the auth context
            interest_level: interestLevel,
            willing_to_pay: willingToPay,
            committed_to_buy: committedToBuy,
            early_bird_commitment: earlyBirdCommitment,
            max_price_willing: willingToPay ? maxPriceWilling : undefined,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          } as ProjectInterest);
        }, 1500);
      } else if (err.message?.includes('not authenticated')) {
        setError('Please log in to express interest in this project.');
      } else {
        setError('Failed to submit interest. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getInterestLevelText = (level: number) => {
    switch (level) {
      case 1: return 'Slightly interested';
      case 2: return 'Somewhat interested';
      case 3: return 'Moderately interested';
      case 4: return 'Very interested';
      case 5: return 'Extremely interested';
      default: return 'Interested';
    }
  };

  // Use centralized pricing service
  const getPricingDisplay = () => {
    return PricingService.generatePricingDisplay(project);
  };

  const getDetailedPricingText = () => {
    return PricingService.getDetailedPricingText(project, t);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="interest-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>
            {project.status === 'released'
              ? `Purchase "${project.title}"`
              : `Express Interest in "${project.title}"`
            }
          </h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {project.status === 'released' ? (
          <div className="purchase-content">
            <div className="project-released-notice">
              <i className="bi bi-check-circle-fill"></i>
              <p>This project has been released and is now available for purchase!</p>
            </div>

            {(() => {
              const pricingText = getDetailedPricingText();
              if (!pricingText) return null;
              return (
                <div className="pricing-info">
                  <h4>Pricing</h4>
                  <div className="price-display">
                    <div className="original-price">{pricingText.originalText}</div>
                    {pricingText.earlyBirdText && (
                      <div className="early-bird-price">{pricingText.earlyBirdText}</div>
                    )}
                    {pricingText.savingsText && (
                      <div className="savings-text">{pricingText.savingsText}</div>
                    )}
                  </div>
                </div>
              );
            })()}

            {existingInterest && existingInterest.committed_to_buy && (
              <div className="prior-commitment-notice">
                <i className="bi bi-heart-fill"></i>
                <p>You previously committed to purchasing this project. Thank you for your support!</p>
                {existingInterest.early_bird_commitment && (
                  <p className="early-bird-note">You committed to early bird pricing.</p>
                )}
              </div>
            )}

            <div className="purchase-actions">
              <button
                type="button"
                className="purchase-btn"
                onClick={() => {
                  if (project.demo_url) {
                    window.open(project.demo_url, '_blank');
                  } else {
                    showToast('Purchase link not yet available. Please contact the project owner.', 'warning');
                  }
                  onClose();
                }}
              >
                <i className="bi bi-cart"></i>
                Purchase Now
              </button>

              {project.demo_url && (
                <a
                  href={project.demo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="visit-project-btn"
                  onClick={onClose}
                >
                  <i className="bi bi-box-arrow-up-right"></i>
                  Visit Project
                </a>
              )}

              <button type="button" onClick={onClose} className="cancel-btn">
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="interest-form">
              <div className="form-group">
                <label>How interested are you in this project?</label>
                <div className="interest-level-selector">
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={interestLevel}
                    onChange={(e) => setInterestLevel(parseInt(e.target.value))}
                    className="interest-slider"
                  />
                  <div className="interest-labels">
                    <span>1</span>
                    <span>2</span>
                    <span>3</span>
                    <span>4</span>
                    <span>5</span>
                  </div>
                  <div className="interest-description">
                    {getInterestLevelText(interestLevel)}
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label willing-to-pay-checkbox">
                  <input
                    type="checkbox"
                    checked={willingToPay}
                    onChange={(e) => setWillingToPay(e.target.checked)}
                    style={{
                      marginRight: '8px',
                      verticalAlign: 'middle',
                      width: '16px',
                      height: '16px'
                    }}
                  />
                  <span style={{ verticalAlign: 'middle' }}>I would be willing to purchase this when released</span>
                  <span
                    className="expand-collapse-icon"
                    style={{
                      marginLeft: '8px',
                      fontSize: '12px',
                      color: '#666',
                      verticalAlign: 'middle'
                    }}
                  >
                    {willingToPay ? '▲' : '▼'}
                  </span>
                </label>
              </div>

              {committedToBuy && project.early_bird_discount && project.early_bird_discount > 0 && (
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={earlyBirdCommitment}
                      onChange={(e) => setEarlyBirdCommitment(e.target.checked)}
                    />
                    I commit to the early bird pricing ({project.early_bird_discount}% discount)
                    <small className="early-bird-note">
                      Lock in the discounted price: ${PricingService.calculateEarlyBirdPrice(project.proposed_price || 0, project.early_bird_discount).toFixed(2)}
                    </small>
                  </label>
                </div>
              )}

              {willingToPay && (
                <div className="form-group">
                  <label>Maximum price you'd be willing to pay:</label>
                  <div className="price-input-group">
                    <span className="currency-symbol">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={maxPriceWilling}
                      onChange={(e) => setMaxPriceWilling(parseFloat(e.target.value) || 0)}
                      className="price-input"
                    />
                  </div>

                  {project.proposed_price && (
                    <div className="price-context">
                      <div className="proposed-price">
                        Expected price: ${project.proposed_price.toFixed(2)}
                      </div>
                      {(() => {
                        const pricingText = getDetailedPricingText();
                        if (pricingText?.earlyBirdText && pricingText?.savingsText) {
                          return (
                            <div className="early-bird-price">
                              {pricingText.earlyBirdText}
                              {pricingText.savingsAmount && (
                                <span className="savings"> ({pricingText.savingsAmount} {t('preReleaseDetail.savings')})</span>
                              )}
                            </div>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Notification Preferences:</label>
                <div className="notification-options">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={platformNotifications}
                      onChange={(e) => setPlatformNotifications(e.target.checked)}
                    />
                    Platform notifications (default)
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={emailNotifications}
                      onChange={(e) => {
                        setEmailNotifications(e.target.checked);
                        if (e.target.checked && !user?.email) {
                          setShowCustomEmail(true);
                        }
                      }}
                    />
                    Email notifications when released
                  </label>
                  {emailNotifications && (
                    <div className="email-input-section">
                      <label>Email address:</label>
                      <input
                        type="email"
                        value={customEmail}
                        onChange={(e) => setCustomEmail(e.target.value)}
                        placeholder="Enter your email address"
                        required={emailNotifications}
                      />
                      {user?.email && user.email !== customEmail && (
                        <button
                          type="button"
                          className="use-account-email-btn"
                          onClick={() => setCustomEmail(user.email)}
                        >
                          Use account email ({user.email})
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <div className="error-message">{error}</div>
              )}

              <div className="modal-actions">
                <button type="button" onClick={onClose} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={loading} className="submit-btn">
                  {loading ? 'Submitting...' : existingInterest ? 'Update Interest' : 'Express Interest'}
                </button>
              </div>
            </form>

            <div className="disclaimer">
              <p>
                <strong>Note:</strong> {committedToBuy
                  ? 'Your commitment helps the creator understand serious purchase intent. While no payment is required now, this indicates your strong intention to buy when released.'
                  : 'This is purely for feedback and market validation. No payment is required now, and there\'s no formal commitment to purchase.'
                }
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};